import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, PackageCheck, Pill } from "lucide-react";

import { medicalService, type MedicalRecord, type MedicalVisit, type Medication, type Prescription } from "../../services/medicalService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ClinicalAlerts, errorLine } from "./ClinicParts";
import { clock, day, dispenseBlock, isFever, medicineName, prescriptionConfig, prescriptionStatus, vitalsLine } from "./medicalLogic";

const FREQUENCIES = ["Once a day", "Twice a day", "Three times a day", "Every 8 hours", "Every 6 hours", "At night", "When needed"];
const DURATIONS = ["1 day", "3 days", "5 days", "7 days", "14 days"];

/**
 * Medicine for a learner, written from a visit that is open or seen. The
 * learner's allergies stay in view while it is written.
 */
export function PrescriptionForm({ visit: fixedVisit, visits, records, medicines, onClose, onDone }: {
  /** The visit it is written from, when opened from one. */
  visit?: MedicalVisit;
  visits: MedicalVisit[];
  records: MedicalRecord[];
  medicines: Medication[];
  onClose: () => void;
  onDone: (prescription: Prescription) => void;
}) {
  const today = todayIso();
  const [visitId, setVisitId] = useState(fixedVisit ? String(fixedVisit.id) : "");
  const [medicineId, setMedicineId] = useState("");
  const [quantity, setQuantity] = useState("");
  const eligible = visits.filter((row) => row.status === "in_progress" || row.status === "completed");
  const visit = fixedVisit ?? eligible.find((row) => String(row.id) === visitId);
  const record = visit ? records.find((row) => row.student === visit.student) : undefined;
  const usable = medicines.filter((row) => row.is_active && !(row.expiry_date && row.expiry_date < today));
  const medicine = usable.find((row) => String(row.id) === medicineId);
  const short = medicine && Number(quantity) > medicine.current_stock;
  const save = useMutation({ mutationFn: medicalService.createPrescription, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!visit) return;
    const data = new FormData(event.currentTarget);
    save.mutate({
      student_id: visit.student,
      medical_visit_id: visit.id,
      medication_id: Number(medicineId),
      dosage: String(data.get("dosage")).trim(),
      frequency: String(data.get("frequency")).trim(),
      duration: String(data.get("duration")).trim(),
      quantity: Number(quantity),
      instructions: String(data.get("instructions") ?? "").trim() || null,
    });
  };

  return (
    <FormModal
      id="new-prescription"
      title={visit ? `Prescribe for ${visit.student_name}` : "New prescription"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The prescription could not be written.") : undefined}
      saveLabel="Write the prescription"
      note="Stock leaves the store only when it is dispensed."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="From the visit" divided={false}>
        {fixedVisit ? (
          <p className="text-sm text-gray-700 sm:col-span-2">
            {day(fixedVisit.visit_date)} at {clock(fixedVisit.visit_time)} · {fixedVisit.chief_complaint}
          </p>
        ) : (
          <Field label="Visit" as="div" className="sm:col-span-2" hint="Only a visit that is open or seen can be prescribed from.">
            <SearchPick
              items={eligible}
              value={visitId}
              onChange={setVisitId}
              label={(row) => `${row.student_name} · ${day(row.visit_date)}`}
              detail={(row) => row.chief_complaint}
              placeholder="Search by learner or complaint"
              empty="No open or seen visits"
              required
            />
          </Field>
        )}
        {visit && (
          <div className="sm:col-span-2">
            <ClinicalAlerts record={record} missingText="No health record for this learner, so allergies are unknown. Ask before prescribing." />
          </div>
        )}
      </FormSection>

      <FormSection title="The medicine">
        <Field label="Medicine" className="sm:col-span-2" hint={medicine ? `${medicine.current_stock} in the store${medicine.dosage_instructions ? ` · usual dose: ${medicine.dosage_instructions}` : ""}` : "Expired and inactive stock is left out."}>
          <select required value={medicineId} onChange={(event) => setMedicineId(event.target.value)} className="input mt-1">
            <option value="">Choose a medicine</option>
            {usable.map((row) => (
              <option key={row.id} value={row.id}>
                {medicineName(row)} · {row.current_stock} in stock
              </option>
            ))}
          </select>
        </Field>
        {medicine?.contraindications && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2">
            <span className="font-semibold">Not for:</span> {medicine.contraindications}
          </p>
        )}
        <Field label="Dose" hint="e.g. one tablet, 5 ml.">
          <input required name="dosage" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="How often">
          <input required name="frequency" list="rx-frequencies" maxLength={100} className="input mt-1" />
          <datalist id="rx-frequencies">{FREQUENCIES.map((value) => <option key={value} value={value} />)}</datalist>
        </Field>
        <Field label="For how long">
          <input required name="duration" list="rx-durations" maxLength={100} className="input mt-1" />
          <datalist id="rx-durations">{DURATIONS.map((value) => <option key={value} value={value} />)}</datalist>
        </Field>
        <Field label="Quantity to give" hint={short ? `Only ${medicine!.current_stock} in stock: it can be written, but not dispensed until more arrives.` : "Tablets, bottles or tubes, as the store counts them."}>
          <input required type="number" min={1} step={1} value={quantity} onChange={(event) => setQuantity(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Instructions" optional className="sm:col-span-2" hint="e.g. after meals, with plenty of water.">
          <textarea name="instructions" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Written, dispensed, on the course, finished. The dispensary works from "To dispense". */
export function PrescriptionsPanel({ prescriptions, visits, records, medicines, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "follow-up" from the dashboard. */
  initialView?: string;
  prescriptions?: Prescription[];
  visits: MedicalVisit[];
  records: MedicalRecord[];
  medicines: Medication[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [writing, setWriting] = useState(false);
  const [reason, setReason] = useState("");
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const dispense = useMutation({ mutationFn: medicalService.dispensePrescription, onSuccess: (row) => done(`${row.quantity} × ${row.medication_name} given to ${row.student_name}; the store count went down by ${row.quantity}.`) });
  const finish = useMutation({ mutationFn: medicalService.completePrescription, onSuccess: (row) => done(`${row.student_name}'s course of ${row.medication_name} is finished.`, true) });
  const cancel = useMutation({ mutationFn: medicalService.cancelPrescription, onSuccess: (row) => done(`The prescription of ${row.medication_name} for ${row.student_name} was cancelled.`, true) });

  const columns: RegisterColumn<Prescription>[] = [
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name, className: "min-w-[10rem]" },
    { header: "Medicine", cell: (row) => row.medication_name, exportValue: (row) => row.medication_name, className: "text-gray-700" },
    { header: "Dose", cell: (row) => `${row.dosage}, ${row.frequency}, ${row.duration}`, exportValue: (row) => `${row.dosage}, ${row.frequency}, ${row.duration}`, className: "min-w-[14rem] whitespace-normal text-gray-700" },
    { header: "Qty", cell: (row) => row.quantity, exportValue: (row) => row.quantity, align: "right" },
    { header: "Prescribed", cell: (row) => day(row.prescribed_date), exportValue: (row) => day(row.prescribed_date), className: "tabular-nums text-gray-700" },
    { header: "By", cell: (row) => row.prescribed_by_name || "—", exportValue: (row) => row.prescribed_by_name ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...prescriptionStatus(row)} />, exportValue: (row) => prescriptionStatus(row).label },
  ];

  const rx = viewing !== null ? prescriptions?.find((row) => row.id === viewing) : undefined;
  const visit = rx ? visits.find((row) => row.id === rx.medical_visit) : undefined;
  const record = rx ? records.find((row) => row.student === rx.student) : undefined;
  const medicine = rx ? medicines.find((row) => row.id === rx.medication) : undefined;
  const blocked = rx ? dispenseBlock(rx, medicine, today) : "";

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Prescriptions"
        description={canManage ? "Open a prescription to dispense it, finish the course or cancel it." : "Medicine written for learners at the sick bay."}
        actions={canManage && (
          <button type="button" onClick={() => setWriting(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Pill className="mr-1.5 h-3.5 w-3.5" /> New prescription
          </button>
        )}
        rows={prescriptions}
        config={prescriptionConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          dispense.reset();
          finish.reset();
          cancel.reset();
          setReason("");
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && row.status === "prescribed" ? "Dispense" : "Open")}
        noun={["prescription", "prescriptions"]}
        exportName={{ file: "sick-bay-prescriptions", title: "Sick Bay Prescriptions" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No prescriptions yet. They are written from a visit."
      />

      {rx && (
        <DetailsModal
          title={`${rx.medication_name} · ${rx.student_name}`}
          headline={{ label: "Dose", value: rx.dosage, note: `${rx.frequency}, for ${rx.duration} · ${rx.quantity} to give` }}
          status={prescriptionStatus(rx)}
          notice={<ClinicalAlerts record={record} />}
          facts={[
            ["Learner", `${rx.student_name} (${rx.student_id})`],
            ["Medicine", rx.medication_name],
            ["In the store now", medicine ? String(medicine.current_stock) : undefined],
            ["Prescribed", `${day(rx.prescribed_date)}${rx.prescribed_by_name ? ` by ${rx.prescribed_by_name}` : ""}`],
            ["Dispensed", day(rx.dispensed_date) || undefined],
            ["Course finished", day(rx.completion_date) || undefined],
            ["Cancelled", day(rx.cancelled_date) || undefined],
            ["From the visit", visit ? `${day(visit.visit_date)} · ${visit.chief_complaint}` : undefined],
            ["Diagnosis", visit?.diagnosis ?? undefined],
            ["Vital signs", visit ? <span className={isFever(visit.vital_signs) ? "text-red-700" : undefined}>{vitalsLine(visit.vital_signs) || "—"}</span> : undefined],
          ]}
          sections={[
            { title: "Instructions", body: rx.instructions },
            { title: "Why it was cancelled", body: rx.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && rx.status === "prescribed" && (
                <button type="button" disabled={Boolean(blocked) || dispense.isPending} onClick={() => dispense.mutate(rx.id)} className="btn btn-primary gap-1.5">
                  <PackageCheck className="h-4 w-4" /> {dispense.isPending ? "Dispensing…" : `Dispense ${rx.quantity}`}
                </button>
              )}
              {canManage && rx.status === "dispensed" && (
                <button type="button" disabled={finish.isPending} onClick={() => finish.mutate(rx.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Course finished
                </button>
              )}
            </>
          }
        >
          {canManage && rx.status === "prescribed" && blocked && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">It cannot be dispensed yet: {blocked}</p>
          )}
          {dispense.isError && errorLine(dispense.error, "The medicine could not be dispensed.")}
          {finish.isError && errorLine(finish.error, "The course could not be marked finished.")}
          {canManage && rx.status === "prescribed" && (
            <ActionPanel title="Cancel the prescription" description="Only before it is dispensed. The reason stays on the learner's record.">
              <Field label="Why it is cancelled" as="div">
                <AIAssistField
                  rows={2}
                  value={reason}
                  onChange={(next) => setReason(next)}
                  placeholder="e.g. The learner is already taking this at home."
                  context={{ key: "medical.cancellation_reason", params: { kind: "prescription", id: rx.id } }}
                  draftLabel="Draft the reason"
                  draftHint="Jot why first, and it writes it up. It sees the medicine, never the learner's health details."
                />
              </Field>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!reason.trim() || cancel.isPending}
                  onClick={() => cancel.mutate({ id: rx.id, reason: reason.trim() })}
                  className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel the prescription
                </button>
              </div>
              {cancel.isError && errorLine(cancel.error, "The prescription could not be cancelled.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {writing && (
        <PrescriptionForm
          visits={visits}
          records={records}
          medicines={medicines}
          onClose={() => setWriting(false)}
          onDone={(row) => {
            setWriting(false);
            done(`${row.medication_name} written for ${row.student_name}. Dispense it when you hand it over.`);
          }}
        />
      )}
    </div>
  );
}
